import apiClient from "./api";
import { getApiAccessToken } from "./UserService";

const baseUrl = "/api/ContractMemo";

// ── Types ──────────────────────────────────────────────────────────────────

export interface ContractMemoContractOption {
  id: number;
  name: string;
  isDefaultContract: boolean;
}

export interface ContractMemoTypeOption {
  id: number;
  description: string;
}

export interface ContractMemoUserOption {
  id: number;
  name: string;
  email?: string | null;
}

export interface ContractMemoFile {
  id: number;
  name: string;
  size: number;
  fileHeader?: string | null;
  created: string;
}

export interface ContractMemoSigning {
  id: number;
  contractMemoId: number;
  requestId?: string | null;
  created: string;
  status: string;
  statusCode: number; // 1 open, 2 in_progress, 3 on_hold, 4 cancelled, 99 complete
  hasSignedFiles: boolean;
  primarySigner: string;
  secondarySigner: string;
  lastStatusUpdate?: string | null;
  releaseStatus: number; // 0 active, 1 released
  released?: string | null;
  releaseComment?: string | null;
}

export interface ContractMemoListItem {
  id: number;
  contractId: number;
  memoDate: string;
  memoTypeId?: number | null;
  memoTypeDescription?: string | null;
  modified?: string | null;
  modifierName?: string | null;
  signingLocked: boolean;
  signing?: ContractMemoSigning | null;
}

export interface ContractMemoDetail {
  id: number;
  contractId: number;
  memoDate: string;
  description?: string | null;
  memoTypeId?: number | null;
  userId?: number | null;
  contractorUserId?: number | null;
  signingLocked: boolean;
  files: ContractMemoFile[];
}

export interface ContractMemoFileUpload {
  fileName: string;
  fileHeader?: string | null;
  fileData: string; // base64 (no data URL prefix)
  contentType?: string | null;
}

export interface SaveContractMemoRequest {
  id?: number | null;
  contractId: number;
  userId: number;
  addressSystemId: number;
  memoDate: string; // ISO date
  description?: string | null;
  memoTypeId: number;
  foremanUserId: number;
  contractorUserId: number;
  files: ContractMemoFileUpload[];
  startSigning: boolean;
}

export interface SaveContractMemoResponse {
  id: number;
  signingStarted: boolean;
  message?: string | null;
  errors: string[];
}

export interface FileDownloadData {
  content: Blob;
  fileName: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const authHeader = async () => ({
  Authorization: `Bearer ${await getApiAccessToken()}`,
});

const extractFileName = (contentDisposition?: string, fallback = "download") => {
  if (!contentDisposition) return fallback;
  const match = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
  if (match && match[1]) {
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }
  return fallback;
};

/** Convert a File to a base64 string without the data-URL prefix. */
export const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1] ?? "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// ── Lookups ────────────────────────────────────────────────────────────────

export const getContracts = async (
  userId: number,
  addressSystemId: number,
): Promise<ContractMemoContractOption[]> => {
  const response = await apiClient.get<ContractMemoContractOption[]>(
    `${baseUrl}/contracts`,
    { params: { userId, addressSystemId }, headers: await authHeader() },
  );
  return response.data;
};

export const getMemoTypes = async (
  addressSystemId: number,
): Promise<ContractMemoTypeOption[]> => {
  const response = await apiClient.get<ContractMemoTypeOption[]>(
    `${baseUrl}/memo-types`,
    { params: { addressSystemId }, headers: await authHeader() },
  );
  return response.data;
};

export const getForemen = async (
  contractId: number,
): Promise<ContractMemoUserOption[]> => {
  const response = await apiClient.get<ContractMemoUserOption[]>(
    `${baseUrl}/foremen`,
    { params: { contractId }, headers: await authHeader() },
  );
  return response.data;
};

export const getContractorForemen = async (
  contractId: number,
): Promise<ContractMemoUserOption[]> => {
  const response = await apiClient.get<ContractMemoUserOption[]>(
    `${baseUrl}/contractor-foremen`,
    { params: { contractId }, headers: await authHeader() },
  );
  return response.data;
};

// ── Memos ──────────────────────────────────────────────────────────────────

export const getMemos = async (
  contractId: number,
  userId: number,
  addressSystemId: number,
): Promise<ContractMemoListItem[]> => {
  const response = await apiClient.get<ContractMemoListItem[]>(
    `${baseUrl}/memos`,
    { params: { contractId, userId, addressSystemId }, headers: await authHeader() },
  );
  return response.data;
};

export const getMemo = async (
  id: number,
  contractId: number,
  userId: number,
  addressSystemId: number,
): Promise<ContractMemoDetail> => {
  const response = await apiClient.get<ContractMemoDetail>(
    `${baseUrl}/memo/${id}`,
    { params: { contractId, userId, addressSystemId }, headers: await authHeader() },
  );
  return response.data;
};

export const saveMemo = async (
  request: SaveContractMemoRequest,
): Promise<SaveContractMemoResponse> => {
  const response = await apiClient.post<SaveContractMemoResponse>(
    `${baseUrl}/save`,
    request,
    { headers: { ...(await authHeader()), "Content-Type": "application/json" } },
  );
  return response.data;
};

export const deleteMemo = async (
  id: number,
  contractId: number,
  userId: number,
  addressSystemId: number,
): Promise<void> => {
  await apiClient.delete(`${baseUrl}/memo/${id}`, {
    params: { contractId, userId, addressSystemId },
    headers: await authHeader(),
  });
};

// ── Files ──────────────────────────────────────────────────────────────────

export const getFiles = async (
  memoId: number,
  contractId: number,
  userId: number,
  addressSystemId: number,
): Promise<ContractMemoFile[]> => {
  const response = await apiClient.get<ContractMemoFile[]>(
    `${baseUrl}/files/${memoId}`,
    { params: { contractId, userId, addressSystemId }, headers: await authHeader() },
  );
  return response.data;
};

export const uploadFiles = async (
  memoId: number,
  contractId: number,
  userId: number,
  addressSystemId: number,
  files: ContractMemoFileUpload[],
): Promise<number> => {
  const response = await apiClient.post<{ uploadedCount: number }>(
    `${baseUrl}/files/upload`,
    { contractMemoId: memoId, contractId, userId, addressSystemId, files },
    { headers: { ...(await authHeader()), "Content-Type": "application/json" } },
  );
  return response.data.uploadedCount;
};

export const downloadFile = async (
  fileId: number,
  memoId: number,
  contractId: number,
  userId: number,
  addressSystemId: number,
): Promise<FileDownloadData> => {
  const response = await apiClient.get<Blob>(
    `${baseUrl}/files/download/${fileId}`,
    {
      params: { memoId, contractId, userId, addressSystemId },
      headers: await authHeader(),
      responseType: "blob",
    },
  );
  return {
    content: response.data as Blob,
    fileName: extractFileName(response.headers["content-disposition"]),
  };
};

export const deleteFile = async (
  fileId: number,
  memoId: number,
  contractId: number,
  userId: number,
  addressSystemId: number,
): Promise<void> => {
  await apiClient.delete(`${baseUrl}/files/${fileId}`, {
    params: { memoId, contractId, userId, addressSystemId },
    headers: await authHeader(),
  });
};

// ── Signing ──────────────────────────────────────────────────────────────────

export const startSigning = async (
  memoId: number,
  contractId: number,
  userId: number,
  addressSystemId: number,
): Promise<ContractMemoSigning> => {
  const response = await apiClient.post<ContractMemoSigning>(
    `${baseUrl}/signing/start`,
    { contractMemoId: memoId, contractId, userId, addressSystemId },
    { headers: { ...(await authHeader()), "Content-Type": "application/json" } },
  );
  return response.data;
};

export const getSigningHistory = async (
  memoId: number,
  contractId: number,
  userId: number,
  addressSystemId: number,
): Promise<ContractMemoSigning[]> => {
  const response = await apiClient.get<ContractMemoSigning[]>(
    `${baseUrl}/signing/history/${memoId}`,
    { params: { contractId, userId, addressSystemId }, headers: await authHeader() },
  );
  return response.data;
};

export const releaseSigning = async (
  memoId: number,
  contractId: number,
  userId: number,
  addressSystemId: number,
  comment: string,
): Promise<void> => {
  await apiClient.post(
    `${baseUrl}/signing/release`,
    { contractMemoId: memoId, contractId, userId, addressSystemId, comment },
    { headers: { ...(await authHeader()), "Content-Type": "application/json" } },
  );
};

export const downloadSignedFile = async (
  signingId: number,
  contractId: number,
  userId: number,
  addressSystemId: number,
): Promise<FileDownloadData> => {
  const response = await apiClient.get<Blob>(
    `${baseUrl}/signing/download/${signingId}`,
    {
      params: { contractId, userId, addressSystemId },
      headers: await authHeader(),
      responseType: "blob",
    },
  );
  return {
    content: response.data as Blob,
    fileName: extractFileName(
      response.headers["content-disposition"],
      `signed_memo_${signingId}.pdf`,
    ),
  };
};
